<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { loadingWrapperLocal } from "@/api/helpers/loadingWrapper";
import { useBaseStore } from "@/stores/base";
import { useExplorerStore } from "@/modules/explorer/store";
import { useDashboardStore } from "@/modules/dashboards/store";
import { filterHasInvalidDataSource } from "@/modules/explorer/components/FilterBar/filterUtils";
import { domainsOfActiveView, domainsOfDocument } from "./domainScope";
import FilterDialog from "./FilterDialog.vue";
import { useFilterDialog } from "./useFilterDialog";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import DimensionDO = App.Data.DimensionDO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;

const { surface } = defineProps<{
	surface: "explorer" | "dashboard";
}>();

const { isFilterDialogOpen, closeFilterDialog } = useFilterDialog();

const isLoading = ref(false);
const availableDimensions = ref<DimensionDO[]>([]);

const isExplorer = computed(() => surface === "explorer");

const canUseSegments = computed(() =>
	Boolean(
		useBaseStore().currentUser?.derivedPermissions.includes(
			"can_manage_segments"
		)
	)
);

// Dashboards offer period filters today; the explorer's period tab is disabled,
// so `datetime` stays out of its list until that changes.
const filterTypes = computed(() => [
	"bucketed",
	"nominal",
	"hierarchy",
	"asset",
	...(isExplorer.value ? ["exception"] : ["datetime"]),
	...(canUseSegments.value ? ["segment"] : []),
]);

const document = computed(() =>
	isExplorer.value ? useExplorerStore().document : null
);

const activeFilters = computed(() =>
	isExplorer.value
		? useExplorerStore().document?.active_filters ?? []
		: useDashboardStore().dashboardState.dashboard.active_filters ?? []
);

const activePins = computed(() =>
	isExplorer.value
		? useExplorerStore().ACTIVE_PINS ?? []
		: useDashboardStore().activeDashboardPins ?? []
);

const selectedFilter = computed(() =>
	isExplorer.value
		? useExplorerStore().explorerState.selectedFilter
		: useDashboardStore().dashboardState.selectedFilter
);

// In the explorer the menu follows whatever is on screen: the graph draws the
// document's verticals, while the data table (and the single metric taken from
// it) can hold metrics from several domains at once.
const explorerDomains = computed(() =>
	isExplorer.value
		? domainsOfActiveView(
				document.value,
				useExplorerStore().explorerState.activeDataView
		  )
		: []
);

// A dashboard spans many verticals, so the dialog offers a domain picker there.
// It starts on the first domain the dashboard's own cards use, falling back to
// the dialog's own first-domain default when the cards say nothing. Seeded once,
// so a choice the user makes afterwards sticks.
const selectedDomain = ref<string | null>(null);
const hasSeededDomain = ref(false);

const dashboardDomains = computed(() => [
	...new Set(
		(useDashboardStore().dashboardState.dashboard?.cards ?? []).flatMap(
			(card) => domainsOfDocument(card.stored_document?.activeDocument ?? null)
		)
	),
]);

const seedDomain = () => {
	if (isExplorer.value || hasSeededDomain.value) {
		return;
	}

	selectedDomain.value = dashboardDomains.value[0] ?? null;
	hasSeededDomain.value = true;
};

const loadDimensions = async () => {
	const client = isExplorer.value ? "explorer" : "dashboard";

	const response = await loadingWrapperLocal(
		useApiClient(client).getDimensions({
			"filter[type]": filterTypes.value,
			"filter[is_filterable]": true,
		}),
		isLoading
	);

	if (response.isOk()) {
		availableDimensions.value = response.value;
	}
};

const invalidDataSource = (dimension: DocumentDimensionDTO): boolean => {
	if (!isExplorer.value) {
		return false;
	}

	return filterHasInvalidDataSource(
		{ dimension, isApplied: true } as ActiveFilterDTO,
		true
	);
};

const applyFilter = (filter: ActiveFilterDTO, mode: "create" | "edit") => {
	if (isExplorer.value) {
		useExplorerStore().APPLY_FILTER(filter, mode);
		return;
	}

	useDashboardStore().applyFilter(filter, mode);
};

const clearSelectedFilter = () => {
	if (isExplorer.value) {
		useExplorerStore().explorerState.selectedFilter = null;
		return;
	}

	useDashboardStore().dashboardState.selectedFilter = null;
};

const onClose = () => {
	clearSelectedFilter();
	closeFilterDialog();
};

watch(
	isFilterDialogOpen,
	(isOpen) => {
		if (!isOpen) {
			return;
		}

		seedDomain();

		if (availableDimensions.value.length === 0) {
			loadDimensions();
		}
	},
	{ immediate: true }
);
</script>
<template>
	<filter-dialog
		v-model="isFilterDialogOpen"
		v-model:domain="selectedDomain"
		:available-dimensions="availableDimensions"
		:active-filters="activeFilters"
		:active-pins="activePins"
		:selected-filter="selectedFilter"
		:document="document"
		:fixed-domains="explorerDomains"
		:api-client="isExplorer ? 'explorer' : 'dashboard'"
		:has-invalid-data-source="invalidDataSource"
		@apply="applyFilter"
		@close="onClose"
	/>
</template>
